import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Trophy, ShieldCheck, ShieldAlert } from 'lucide-react'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { SemnaturaPad } from '../SemnaturaPad'
import { useState } from 'react'
import { deschideFisier } from '../../lib/downloadFile'

/**
 * Extragerile unei tombole.
 *
 * Fluxul, în formularea din regulament: la prima extragere intră toate înscrierile de la
 * deschiderea formularului; după fiecare extragere baza se închide, iar la următoarea intră doar
 * cine s-a înscris de atunci încoace. Șapte extrageri pe zi, la momente alese de Organizator.
 *
 * Deci ecranul are un singur buton, nu o listă de ore: apeși *Extrage acum* și gata — serverul
 * stabilește fereastra, cere numărul la random.org și scrie câștigătorul.
 *
 * Prima variantă îți dădea intervalul, te trimitea pe random.org și te punea să scrii numărul
 * înapoi într-un câmp. Alexandru: „As vrea sa fie automat si needitabil". Avea dreptate — un
 * număr tastat de om nu dovedește nimic, oricât de corect ar fi omul. Aici nu există câmp de
 * scris numărul, nici în interfață, nici în API.
 */

/** Baza deschisă acum: de la ultima extragere până în clipa asta. */
interface Fereastra {
  oraStart: string
  nrDeLa: number | null
  nrPanaLa: number | null
  cati: number
  extrageriAzi: number
  extrageriTotal: number
  reextrageri: number
  extrageriPeZi: number
  extrageriTotalMax: number
  comisie: string[]
}

interface Extragere {
  id: number
  oraStart: string
  oraFinal: string
  nrDeLa: number
  nrPanaLa: number
  nrExtras: number
  sursa: string
  areDovada: boolean
  numereSarite: number[]
  eReextragere: boolean
  catiEligibili: number | null
  /** Adresa inregistrarii de la random.org. Gol = extragerea a cazut pe calea de rezerva. */
  drawRecordUrl: string | null
  areSemnaturi: boolean
  /** Premiul extras dar neridicat — art. 6.3. Extragerea rămâne, premiul se reportează. */
  /** Premiul a fost predat? Atunci „neridicat" nu se mai poate marca. */
  premiuPredat: boolean
  neridicatLa: string | null
  motivNeridicare: string | null
  nota: string | null
  castigator: string
  telefon: string | null
  email: string | null
  nrPv: string
}

interface Inscriere {
  nrOrdine: number
  predareId: number
  nrPv: string
  la: string
  nume: string
  telefon: string | null
  castigatorLa: string | null
  anulataLa: string | null
  /** Și-a cerut ștergerea datelor. Rămâne numerotat, dar nu poate câștiga. */
  anonimizatLa: string | null
}

function mesaj(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'A apărut o eroare'
}

function ora(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}`
}

function ziSiOra(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * Cât a trecut între extragere și momentul în care premiul a fost declarat neridicat.
 *
 * Alexandru: „să se vadă clar că au trecut 2 ore între data extragerii și data la care a fost
 * marcat ca premiu neridicat".
 *
 * Nu e o înflorire: art. 6.2 dă exact două ore, calculate de la ora extragerii consemnată în CRM
 * și pe procesul-verbal. Cine contestă mai târziu întreabă fix asta — cât a trecut —, iar
 * răspunsul trebuie să fie pe ecran, nu socotit din două ore scrise separat.
 */
function dupaCatTimp(extrasLa: string, marcatLa: string): { text: string; peste: boolean } {
  const min = Math.round((new Date(marcatLa).getTime() - new Date(extrasLa).getTime()) / 60_000)
  const h = Math.floor(min / 60)
  const durata = h > 0 ? `${h}h ${min % 60}m` : `${min} min`
  return { text: durata, peste: min >= 120 }
}

/**
 * Trimite semnătura la formularul public de verificare al celor de la random.org.
 *
 * Alexandru: „Aici putem avea si ceva de pe random.org, ca si dovada pentru corectitudinea
 * tragerii?" Asta e dovada: nu o afirmație de-a noastră, ci un răspuns semnat de ei, pe care
 * oricine — inclusiv un participant nemulțumit — îl poate duce la ei și verifica.
 *
 * Se trimite printr-un formular construit pe loc, nu printr-un link: verificarea lor primește
 * datele prin POST, iar semnătura e prea lungă pentru un URL.
 */
async function verificaLaRandomOrg(id: number) {
  const dovada = (
    await api.get<{ random: unknown; signature: string }>(
      `/marketing/sampling/extrageri/${id}/dovada`,
    )
  ).data

  const f = document.createElement('form')
  f.method = 'POST'
  f.action = 'https://api.random.org/signatures/form'
  f.target = '_blank'
  f.rel = 'noreferrer'
  for (const [nume, valoare] of [
    ['format', 'json'],
    ['random', JSON.stringify(dovada.random)],
    ['signature', dovada.signature],
  ]) {
    const i = document.createElement('input')
    i.type = 'hidden'
    i.name = nume
    i.value = valoare
    f.appendChild(i)
  }
  document.body.appendChild(f)
  f.submit()
  f.remove()
}

/** O extragere făcută local, fiindcă random.org n-a răspuns, trebuie să se vadă ca atare. */
function esteLocal(sursa: string): boolean {
  return sursa.startsWith('local')
}

export function TombolaExtrageri({
  actiuneId,
  canWrite,
}: {
  actiuneId: number
  canWrite: boolean
}) {
  const queryClient = useQueryClient()
  /*
    Extragerea pe care se semneaza acum, si semnaturile stranse pana la apasarea butonului.

    Se semneaza DUPA ce extragerea s-a tinut, nu inainte: cele doua ore ale castigatorului curg din
    clipa extragerii, iar o mana ocupata n-are voie sa tina in loc o tragere la sorti.
  */
  const [deSemnat, setDeSemnat] = useState<Extragere | null>(null)
  const [semnaturi, setSemnaturi] = useState<Record<string, string>>({})

  /*
    Premiul extras dar neridicat. Victor, in seara primei zile: „sunt castigatori care nu s-au
    prezentat sa ridice premiul (...) sa putem marca in platforma ca nu a indeplinit conditia".

    Extragerea nu se sterge: a avut loc, e inregistrata la random.org si are proces-verbal. Se
    consemneaza doar soarta premiului.
  */
  const neridicat = useMutation({
    mutationFn: async (v: { id: number; neridicat: boolean; motiv?: string | null }) =>
      api.post(`/marketing/sampling/extrageri/${v.id}/neridicat`, {
        neridicat: v.neridicat,
        motiv: v.motiv ?? null,
      }),
    onSuccess: (_d, v) => {
      toast.success(v.neridicat ? 'Marcat ca neridicat' : 'Marcajul a fost scos')
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'extrageri'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const semneaza = useMutation({
    mutationFn: async (v: { id: number; semnaturi: { nume: string; imagine: string }[] }) =>
      api.post(`/marketing/sampling/extrageri/${v.id}/semnaturi`, { semnaturi: v.semnaturi }),
    onSuccess: () => {
      toast.success('Semnături înregistrate. Procesul-verbal le include de acum.')
      setDeSemnat(null)
      setSemnaturi({})
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'extrageri'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const fereastra = useQuery({
    queryKey: ['sampling', 'fereastra', actiuneId],
    queryFn: async () =>
      (await api.get<Fereastra>(`/marketing/sampling/actiuni/${actiuneId}/fereastra`)).data,
    // La stand se înscrie lume tot timpul, iar baza deschisă crește cu ei. Cifra de pe buton
    // trebuie să fie a momentului, nu a minutului în care s-a deschis panoul.
    refetchInterval: 20_000,
  })

  const extrageri = useQuery({
    queryKey: ['sampling', 'extrageri', actiuneId],
    queryFn: async () =>
      (await api.get<Extragere[]>(`/marketing/sampling/actiuni/${actiuneId}/extrageri`)).data,
  })

  const inscrieri = useQuery({
    queryKey: ['sampling', 'inscrieri', actiuneId],
    queryFn: async () =>
      (await api.get<Inscriere[]>(`/marketing/sampling/actiuni/${actiuneId}/inscrieri`)).data,
  })

  const extrage = useMutation({
    mutationFn: async (eReextragere: boolean) => {
      const r = await api.post<{
        nrExtras: number
        sursa: string
        numereSarite: number[]
        castigator: { nume: string; telefon: string | null }
      }>(
        `/marketing/sampling/actiuni/${actiuneId}/extrageri`,
        { eReextragere },
        // Apelul la random.org poate dura, iar o re-extragere peste o înscriere anulată îl repetă.
        { timeout: 45_000 },
      )
      return r.data
    },
    onSuccess: (r) => {
      toast.success(`Numărul ${r.nrExtras} — ${r.castigator.nume}`)
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const total = inscrieri.data?.length ?? 0

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-b4l-bg p-3 text-sm text-b4l-text-muted">
        <strong className="text-b4l-text">{total}</strong> înscriși ·{' '}
        <strong className="text-b4l-text">{extrageri.data?.length ?? 0}</strong> extrageri făcute.
        Numerele sunt pe tombola asta, de la 1, în ordinea înscrierii.
      </div>

      {/*
        UN SINGUR BUTON, nu o listă de ore.

        Regulamentul leagă extragerile una de alta, nu de ceas: baza deschisă merge de la extragerea
        precedentă până în clipa apăsării. Nu există „fereastra de la 11" pe care s-o alegi — există
        doar ce s-a strâns de la ultima extragere încoace. Un ecran care ar pune la alegere
        intervalul ar contrazice chiar textul care se autentifică notarial.
      */}
      <div className="rounded-lg border border-b4l-border p-3">
        {fereastra.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : !fereastra.data ? (
          <p className="text-sm text-b4l-text-muted">Nicio înscriere încă.</p>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-b4l-text">
                  Baza deschisă: <strong>{fereastra.data.cati}</strong>{' '}
                  {fereastra.data.cati === 1 ? 'înscris' : 'înscriși'}
                  {fereastra.data.nrDeLa !== null && (
                    <>
                      {' '}
                      · numerele{' '}
                      <span className="font-mono">
                        {fereastra.data.nrDeLa}–{fereastra.data.nrPanaLa}
                      </span>
                    </>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-b4l-text-muted">
                  De la {ziSiOra(fereastra.data.oraStart)} până acum. Extragerea închide baza:
                  următoarea pornește de la cine se înscrie după ea.
                </p>
              </div>

              {canWrite && (
                <Button
                  disabled={
                    extrage.isPending ||
                    fereastra.data.cati === 0 ||
                    fereastra.data.extrageriTotal >= fereastra.data.extrageriTotalMax
                  }
                  /*
                    Confirmare inainte de extragere. Alexandru: „e posibil sa fii apasat din
                    greseala ma gandesc".

                    Butonul consuma unul din paisprezece premii, iar apasarea nu se poate lua
                    inapoi: numarul vine de la un tert si e deja inregistrat la el in clipa in care
                    a raspuns. Nu exista „anuleaza extragerea".

                    Intrebarea spune CE se pierde daca a fost din greseala — cati sunt in baza si
                    ce numere —, nu doar „esti sigur?". Un „esti sigur" pe care il vezi de
                    paisprezece ori nu mai opreste pe nimeni.
                  */
                  onClick={() => {
                    const f = fereastra.data
                    if (!f) return
                    const ramase = f.extrageriTotalMax - f.extrageriTotal
                    if (
                      confirm(
                        `Extragi acum din baza deschisă?

` +
                          `${f.cati} înscriși${f.nrDeLa !== null ? ` · numerele ${f.nrDeLa}–${f.nrPanaLa}` : ''}
` +
                          `Rămân ${ramase} extrageri din cele ${f.extrageriTotalMax}.

` +
                          `Numărul vine de la random.org și se înregistrează la ei. Extragerea nu se poate anula.`,
                      )
                    ) {
                      extrage.mutate(false)
                    }
                  }}
                >
                  {extrage.isPending ? 'Se extrage…' : 'Extrage acum'}
                </Button>
              )}
            </div>

            <p className="mt-2 text-xs text-b4l-text-muted">
              Extrageri azi:{' '}
              <strong className="text-b4l-text">{fereastra.data.extrageriAzi}</strong> din{' '}
              {fereastra.data.extrageriPeZi} · pe campanie{' '}
              <strong className="text-b4l-text">{fereastra.data.extrageriTotal}</strong> din{' '}
              {fereastra.data.extrageriTotalMax}
              {fereastra.data.reextrageri > 0 && ` · ${fereastra.data.reextrageri} reextrageri`}.
              Numărul îl cere serverul la random.org și se scrie singur — nu se poate tasta și nu se
              poate schimba după.
            </p>

            {/*
              REEXTRAGEREA, ca buton separat.

              Art. 4.7: cand un premiu nu e ridicat in cele doua ore, se reextrage dintr-o baza noua.
              Mecanic e aceeasi apasare — baza deschisa e deja noua si neutilizata. Se schimba doar
              numaratoarea: art. 4.4 spune ca plafonul de paisprezece e „fara a include eventualele
              reextrageri". De aceea nu poate fi acelasi buton: cele doua consuma lucruri diferite.
            */}
            {canWrite && fereastra.data.cati > 0 && (
              <p className="mt-2 text-xs text-b4l-text-muted">
                Un premiu neridicat în cele două ore?{' '}
                <button
                  onClick={() => extrage.mutate(true)}
                  disabled={extrage.isPending}
                  className="underline disabled:opacity-50"
                >
                  Reextrage
                </button>{' '}
                — nu consumă una dintre cele {fereastra.data.extrageriTotalMax}.
              </p>
            )}

            {/*
              Sapte pe zi e o TINTA, nu un plafon: art. 4.4 lasa extragerile nefacute in prima zi sa
              se reporteze in a doua, „fara ca totalul (...) sa depaseasca 14". Deci aici se
              informeaza, nu se blocheaza — blocajul e pe total, mai jos.
            */}
            {fereastra.data.extrageriAzi >= fereastra.data.extrageriPeZi &&
              fereastra.data.extrageriTotal < fereastra.data.extrageriTotalMax && (
                <p className="mt-2 rounded-lg bg-b4l-amber-light p-2 text-xs text-b4l-amber-dark">
                  S-au făcut cele {fereastra.data.extrageriPeZi} extrageri alocate zilei de azi. Se
                  poate continua: art. 4.4 permite reportarea, cât timp totalul campaniei rămâne sub{' '}
                  {fereastra.data.extrageriTotalMax}.
                </p>
              )}

            {fereastra.data.extrageriTotal >= fereastra.data.extrageriTotalMax && (
              <p className="mt-2 rounded-lg bg-b4l-amber-light p-2 text-xs text-b4l-amber-dark">
                S-au făcut cele {fereastra.data.extrageriTotalMax} extrageri ale campaniei, câte
                premii sunt. Pentru un premiu neridicat, folosește reextragerea.
              </p>
            )}

            {fereastra.data.cati === 0 && (
              <p className="mt-2 text-xs text-b4l-text-muted">
                Nicio înscriere nouă de la extragerea precedentă — nu e din ce extrage.
              </p>
            )}
          </>
        )}
      </div>

      {(extrageri.data?.length ?? 0) > 0 && (
        <div>
          <p className="text-sm font-medium text-b4l-text">Câștigători</p>
          <div className="mt-2 divide-y divide-b4l-border rounded-lg border border-b4l-border">
            {extrageri.data?.map((e) => (
              <div key={e.id} className="p-2.5 text-sm">
                <div className="flex items-center gap-2">
                  <Trophy size={14} className="text-b4l-amber" />
                  <span className="font-medium text-b4l-text">{e.castigator}</span>
                  <span className="text-xs text-b4l-text-muted">{e.telefon ?? e.email ?? ''}</span>
                  {/*
                    Inregistrarea la tert bate semnatura. Alexandru, la prima extragere reala:
                    randul spunea „fara semnatura — nu se poate verifica", desi extragerea avea
                    Drawing #299922 la random.org, cu 73 de participanti si inregistrare pastrata
                    cinci ani.

                    Eticheta era un rest din vechea cale: cand extragerea trecea prin Signed API,
                    dovada era semnatura raspunsului. Prin Third-Party Draw Service dovada e mai
                    tare — o tine TERTUL, nu noi —, dar `dovada` ramane goala, si conditia de aici
                    o citea ca pe o lipsa.

                    Un ecran care declara nedovedita o extragere dovedita e mai rau decat unul
                    tacut: te face sa cauti o problema care nu exista, in mijlocul evenimentului.
                  */}
                  {e.drawRecordUrl ? (
                    <a
                      href={e.drawRecordUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs text-b4l-green-dark underline"
                      title="Înregistrarea extragerii la random.org — o poate deschide oricine își știe numărul de înscriere"
                    >
                      <ShieldCheck size={12} /> înregistrare la random.org
                    </a>
                  ) : e.areDovada ? (
                    <button
                      onClick={() =>
                        verificaLaRandomOrg(e.id).catch((err) => toast.error(mesaj(err)))
                      }
                      className="flex items-center gap-1 text-xs text-b4l-green-dark underline"
                      title="Deschide verificarea la random.org, cu semnătura acestei extrageri"
                    >
                      <ShieldCheck size={12} /> verifică la random.org
                    </button>
                  ) : (
                    /*
                      Fara dovada, se SPUNE. Doua cazuri, si niciunul nu trebuie sa treaca tacut:
                      numarul generat local fiindca random.org n-a raspuns, si numarul luat de la
                      ei dar nesemnat, fiindca cheia API lipseste sau e gresita.

                      Al doilea a stat ascuns pana pe 28.08: apelul semnat cadea in tacere pe cel
                      nesemnat, extragerea reusea, iar lipsa dovezii se vedea abia pe pagina
                      publica. La eveniment ar fi insemnat extrageri fara nimic de verificat.
                    */
                    <span
                      className="flex items-center gap-1 text-xs text-b4l-amber-dark"
                      title={
                        esteLocal(e.sursa)
                          ? 'random.org nu a răspuns; numărul a fost generat pe serverul nostru'
                          : 'Numărul vine de la random.org, dar fără semnătură — cheia API lipsește sau e greșită. Nimeni nu poate verifica extragerea.'
                      }
                    >
                      <ShieldAlert size={12} />{' '}
                      {esteLocal(e.sursa) ? 'local' : 'fără semnătură — nu se poate verifica'}
                    </span>
                  )}
                </div>
                {/*
                  Procesul-verbal al extragerii, deschis din CRM. Art. 4.6 il cere la fiecare
                  extragere; pana acum datele existau in baza, dar documentul trebuia scris de mana
                  din ce se vedea pe ecran, de paisprezece ori intr-o zi aglomerata.

                  `inline`, deci se deschide in fila, nu se descarca orbeste — se vede intai, si
                  cine vrea fisierul il salveaza din vizualizator.
                */}
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
                  <button
                    onClick={() =>
                      void deschideFisier(`/marketing/sampling/extrageri/${e.id}/pv`).catch(
                        (err: Error) => toast.error(err.message),
                      )
                    }
                    className="font-medium text-b4l-blue underline"
                  >
                    Proces-verbal ↗
                  </button>
                  {/*
                    Semnat o data, nu se mai deschide. Alexandru: „desi PV extragere e semnat,
                    permite semnarea inca odata, ar trebui blocat". Butonul arata ca butonul si
                    invita la apasat; ce e gata ramane o eticheta.
                  */}
                  {canWrite &&
                    (e.areSemnaturi ? (
                      <span className="text-b4l-green-dark">semnat ✓</span>
                    ) : (
                      <button
                        onClick={() => {
                          setSemnaturi({})
                          setDeSemnat(e)
                        }}
                        className="text-b4l-amber-dark underline"
                      >
                        de semnat
                      </button>
                    ))}
                  {/*
                    Premiul neridicat. Se apasa la stand, cand cele doua ore au trecut si omul n-a
                    venit — art. 6.3. Butonul nu sterge nimic: extragerea ramane, cu inregistrarea
                    ei la random.org, si se consemneaza doar ca premiul n-a fost luat.

                    Se poate desface, cat timp actiunea e deschisa: cineva poate ajunge la stand in
                    minutul in care s-a apasat.
                  */}
                  {/*
                    Nimic de marcat cand premiul a fost predat: exista proces-verbal semnat, marfa a
                    iesit pe bon, mesajul a plecat. Alexandru: „la cei care au premiul ridicat,
                    dovedit cu PV, nu ar trebui sa existe optiune de marcheaza neridicat".
                  */}
                  {canWrite &&
                    !e.premiuPredat &&
                    (e.neridicatLa ? (
                      <button
                        onClick={() => neridicat.mutate({ id: e.id, neridicat: false })}
                        className="text-b4l-red underline"
                        title={
                          e.motivNeridicare
                            ? `Neridicat: ${e.motivNeridicare}. Apasă ca să scoți marcajul.`
                            : 'Apasă ca să scoți marcajul de premiu neridicat.'
                        }
                      >
                        premiu neridicat
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          const motiv = prompt(
                            'Premiul nu a fost ridicat. Scrie pe scurt de ce (apare pe procesul-verbal, nu public):',
                            'Nu s-a prezentat în cele două ore',
                          )
                          if (motiv === null) return
                          neridicat.mutate({ id: e.id, neridicat: true, motiv })
                        }}
                        className="text-b4l-text-muted underline"
                      >
                        marchează neridicat
                      </button>
                    ))}
                  {e.drawRecordUrl && (
                    <a
                      href={e.drawRecordUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-b4l-green-dark underline"
                    >
                      înregistrarea la random.org ↗
                    </a>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-b4l-text-muted">
                  {e.eReextragere && <strong className="text-b4l-text">reextragere · </strong>}
                  {ziSiOra(e.oraStart)}–{ora(e.oraFinal)} · numărul{' '}
                  <span className="font-mono">{e.nrExtras}</span> din {e.nrDeLa}–{e.nrPanaLa}
                  {e.catiEligibili !== null && ` · ${e.catiEligibili} înscrieri validate`} ·{' '}
                  {e.sursa}
                  {e.numereSarite.length > 0 &&
                    ` · re-extras peste ${e.numereSarite.join(', ')} (înscrieri anulate)`}
                  {e.nota && ` · ${e.nota}`}
                </div>
                {/*
                  Cronologia premiului neridicat, scrisa pe rand. Alexandru: „sa se vada in plus si
                  data si ora la care a fost extragerea, data la care a fost marcat ca premiu
                  neridicat (...) si mesaj ca a depasit termenul de 2 ore prevazut in regulament".

                  Cele doua ore ale art. 6.2 curg de la ora extragerii. Scrise una langa alta, cu
                  diferenta calculata, nu mai trebuie sa scada nimeni din minte in fata unei
                  contestatii.
                */}
                {e.neridicatLa && (
                  <div className="mt-1 rounded bg-b4l-amber-light px-2 py-1 text-xs text-b4l-amber-dark">
                    Extras {ziSiOra(e.oraFinal)} · marcat neridicat {ziSiOra(e.neridicatLa)} ·{' '}
                    <strong>{dupaCatTimp(e.oraFinal, e.neridicatLa).text}</strong>
                    {dupaCatTimp(e.oraFinal, e.neridicatLa).peste
                      ? ' — peste termenul de 2 ore din regulament (art. 6.2)'
                      : ' — sub termenul de 2 ore din regulament (art. 6.2)'}
                    {e.motivNeridicare && ` · ${e.motivNeridicare}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/*
        SEMNAREA COMISIEI.

        Alexandru: „nu vreau sa tiparim procesele verbale ale extragerii si atunci membri comisiei ar
        trebui sa semneze pe ecran". Pana acum PV-ul iesea cu linii goale — machetate pentru pix, pe
        un document care nu se tipareste niciodata.

        Se semneaza pe rand, cate un membru, pe acelasi telefon sau tableta. Numele vin din Setari,
        deci nu se tasteaza de paisprezece ori.
      */}
      {deSemnat && (
        <Modal
          open
          onClose={() => setDeSemnat(null)}
          title={`Semnături — extragerea numărului ${deSemnat.nrExtras}`}
        >
          {(fereastra.data?.comisie.length ?? 0) === 0 ? (
            <p className="text-sm text-b4l-red">
              Comisia nu e configurată. Scrie membrii în Sampling → Setări, câte unul pe rând.
            </p>
          ) : (
            <>
              <p className="text-sm text-b4l-text-muted">
                Semnează fiecare membru al comisiei, pe rând, pe același ecran. Semnăturile intră în
                procesul-verbal al acestei extrageri.
              </p>
              {fereastra.data?.comisie.map((nume) => (
                <div key={nume} className="mt-4">
                  <p className="text-sm font-medium text-b4l-text">{nume}</p>
                  <div className="mt-1">
                    <SemnaturaPad
                      onChange={(img) =>
                        setSemnaturi((x) => {
                          const nou = { ...x }
                          if (img) nou[nume] = img
                          else delete nou[nume]
                          return nou
                        })
                      }
                      disabled={semneaza.isPending}
                    />
                  </div>
                </div>
              ))}
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setDeSemnat(null)}>
                  Renunță
                </Button>
                <Button
                  disabled={
                    semneaza.isPending ||
                    Object.keys(semnaturi).length !== (fereastra.data?.comisie.length ?? 0)
                  }
                  onClick={() =>
                    semneaza.mutate({
                      id: deSemnat.id,
                      semnaturi: Object.entries(semnaturi).map(([nume, imagine]) => ({
                        nume,
                        imagine,
                      })),
                    })
                  }
                >
                  {semneaza.isPending ? 'Se salvează…' : 'Salvează semnăturile'}
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}

      {(inscrieri.data?.length ?? 0) > 0 && (
        <details>
          <summary className="cursor-pointer text-sm font-medium text-b4l-text">
            Toți înscrișii ({total})
          </summary>
          <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-b4l-border">
            {inscrieri.data?.map((i) => (
              <div
                key={i.predareId ?? i.nrOrdine}
                className="flex items-center gap-2 border-b border-b4l-border px-3 py-1.5 text-xs last:border-0"
              >
                <span className="w-10 font-mono text-b4l-text-dim">{i.nrOrdine}</span>
                {/*
                  Taiat = nu poate castiga. Doua motive, si se vede care: retras, sau cu datele
                  sterse la cerere. Numarul ramane al lui in ambele cazuri.
                */}
                <span
                  className={`flex-1 ${
                    i.anulataLa || i.anonimizatLa
                      ? 'text-b4l-text-dim line-through'
                      : 'text-b4l-text'
                  }`}
                >
                  {i.nume}
                </span>
                {i.anonimizatLa && !i.anulataLa && (
                  <span className="shrink-0 text-b4l-text-dim">date șterse</span>
                )}
                <span className="text-b4l-text-muted">{i.telefon ?? ''}</span>
                <span className="w-24 text-right text-b4l-text-dim">{ziSiOra(i.la)}</span>
                {i.castigatorLa && <Trophy size={12} className="text-b4l-amber" />}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}
